// src/components/Dashboard/RoleDashboard/RoleExtras/ViewPosts.jsx
import React, { useEffect, useState } from "react";
import { apiFetch } from "../../../../apiFetch";
import { useParams, useNavigate } from "react-router-dom";
import { resolveId } from "../roleApi";
import "./ViewPosts.css";

const VALID_ROLES = ["agent", "scout", "club"];
const isDev =
    typeof process !== "undefined"
        ? process.env.NODE_ENV !== "production"
        : true;

function useRoleAndId(props) {
    const params = useParams();

    let role = (props?.role || params.role || "").toLowerCase();
    if (!VALID_ROLES.includes(role)) {
        const storedRole =
            (typeof window !== "undefined"
                ? (localStorage.getItem("userRole") ||
                    localStorage.getItem("role") ||
                    "")
                : ""
            ).toLowerCase() || "agent";
        role = VALID_ROLES.includes(storedRole) ? storedRole : "agent";
    }

    const rawId = props?.id || params.id || params.userId;
    const id = resolveId({
        role,
        id: rawId,
        agentId: props?.agentId,
        scoutId: props?.scoutId,
        clubId: props?.clubId,
        userId: props?.userId,
    });

    if (isDev) console.debug("[Role ViewPosts] role/id", { role, rawId, id, params });

    return { role, id };
}

function formatTimestamp(timestamp) {
    if (!timestamp) return "Not Available";
    const d = new Date(timestamp);
    if (Number.isNaN(d.getTime())) return "Not Available";
    return d.toLocaleString();
}

export default function ViewPosts(props) {
    const { role, id } = useRoleAndId(props);
    const navigate = useNavigate();

    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editPostId, setEditPostId] = useState(null);
    const [editDescription, setEditDescription] = useState("");
    const [editLink, setEditLink] = useState("");
    const [error, setError] = useState("");


    useEffect(() => {
        let ignore = false;

        const fetchPosts = async () => {
            if (!role || !id) {
                setLoading(false);
                setError("Missing role or id.");
                return;
            }

            try {
                setLoading(true);
                setError("");

                // unified multi-role endpoint
                const path = `/api/opportunities?ownerRole=${encodeURIComponent(role)}&ownerId=${encodeURIComponent(id)}`;

                const res = await apiFetch(path);



                if (!res.ok) {
                    const text = await res.text().catch(() => "");
                    throw new Error(
                        `Failed to fetch opportunities (${res.status}). ${text.slice(
                            0,
                            200
                        )}`
                    );
                }

                const data = await res.json();
                if (ignore) return;

                const list = Array.isArray(data) ? data : [];
                const normalized = list.map((p) => ({
                    ...p,
                    id: p.id || p._id,
                }));

                if (isDev)
                    console.log("[Role ViewPosts] loaded opportunities:", normalized);

                setPosts(normalized);
            } catch (err) {
                if (!ignore) {
                    console.error("❌ Error fetching posts:", err);
                    setError(err.message || "Failed to load opportunities.");
                }
            } finally {
                if (!ignore) setLoading(false);
            }
        };

        fetchPosts();
        return () => {
            ignore = true;
        };
    }, [role, id]);

    const handleDelete = async (postId) => {
        if (!window.confirm("Are you sure you want to delete this post?")) return;

        try {
            const res = await apiFetch(`/api/opportunities/${postId}`, {
                method: "DELETE",
            });

            if (!res.ok) throw new Error(`Delete failed (${res.status})`);




            setPosts((prev) =>
                prev.map((post) =>
                    post.id === postId ? { ...post, status: "Deleted" } : post
                )
            );

            alert("✅ Post marked as deleted");
        } catch (err) {
            console.error(err);
            alert("❌ Failed to delete post");
        }
    };

    const handleToggleStatus = async (postId, currentStatus) => {
        const newStatus = currentStatus === "Open" ? "Closed" : "Open";
        try {
            await apiFetch(`/api/opportunities/${postId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ status: newStatus }),
            });



            setPosts((prev) =>
                prev.map((post) =>
                    post.id === postId ? { ...post, status: newStatus } : post
                )
            );
        } catch (err) {
            console.error(err);
            alert("❌ Failed to update status");
        }
    };

    const handleEdit = (post) => {
        setEditPostId(post.id);
        setEditDescription(post.description || "");
        setEditLink(post.link || "");
    };

    const handleSaveEdit = async () => {
        try {
            await apiFetch(`/api/opportunities/${editPostId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    description: editDescription,
                    link: editLink,
                }),
            });



            setPosts((prev) =>
                prev.map((post) =>
                    post.id === editPostId
                        ? { ...post, description: editDescription, link: editLink }
                        : post
                )
            );

            setEditPostId(null);
            setEditDescription("");
            setEditLink("");
        } catch (err) {
            console.error(err);
            alert("❌ Failed to save changes");
        }
    };

    if (!role || !id) {
        return (
            <div className="role-view-posts">
                <div className="posts-container">
                    <p className="loading-text">Missing role or id.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="role-view-posts">
            <div className="posts-container">
                <h2 className="posts-title">Posted Opportunities</h2>

                {loading ? (
                    <p className="loading-text">Loading...</p>
                ) : error ? (
                    <p className="loading-text" style={{ color: "#ff4d4d" }}>
                        {error}
                    </p>
                ) : posts.length === 0 ? (
                    <p className="no-posts-text">No opportunities posted yet.</p>
                ) : (
                    posts.map((post) => {
                        const isDeleted = post.status === "Deleted";

                        return (
                            <div
                                key={post.id}
                                className={`post-card ${isDeleted ? "post-deleted" : ""}`}
                            >
                                {editPostId === post.id ? (
                                    <>
                                        <textarea
                                            value={editDescription}
                                            onChange={(e) =>
                                                setEditDescription(e.target.value)
                                            }
                                        />
                                        <input
                                            type="text"
                                            value={editLink}
                                            onChange={(e) =>
                                                setEditLink(e.target.value)
                                            }
                                        />
                                        <div className="post-actions">
                                            <button
                                                className="edit-btn"
                                                onClick={handleSaveEdit}
                                            >
                                                Save
                                            </button>
                                            <button
                                                className="delete-btn"
                                                onClick={() => setEditPostId(null)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <h3 className="post-card-title">
                                            Posted Opportunity
                                        </h3>

                                        <p className="post-description">
                                            <strong>Opportunity:</strong>{" "}
                                            {post.description || "No description"}
                                        </p>

                                        {post.link && (
                                            <p className="post-description">
                                                <strong>Link:</strong>{" "}
                                                <a
                                                    href={
                                                        /^https?:\/\//i.test(post.link)
                                                            ? post.link
                                                            : `https://${post.link}`
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="post-link"
                                                >
                                                    {post.link}
                                                </a>
                                            </p>
                                        )}

                                        <p className="post-time">
                                            <strong>Posted at:</strong>{" "}
                                            {formatTimestamp(post.createdAt)}
                                        </p>

                                        <p className="post-status">
                                            <strong>Status:</strong>{" "}
                                            {post.status || "Open"}
                                        </p>

                                        {isDeleted && (
                                            <p className="deleted-label">[Deleted]</p>
                                        )}

                                        <div className="post-actions">
                                            <button
                                                className="edit-btn"
                                                onClick={() => handleEdit(post)}
                                                disabled={isDeleted}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="delete-btn"
                                                onClick={() => handleDelete(post.id)}
                                                disabled={isDeleted}
                                            >
                                                Delete
                                            </button>
                                            <button
                                                className="toggle-btn"
                                                onClick={() =>
                                                    handleToggleStatus(
                                                        post.id,
                                                        post.status || "Open"
                                                    )
                                                }
                                                disabled={isDeleted}
                                            >
                                                {post.status === "Closed"
                                                    ? "Reopen"
                                                    : "Close"}
                                            </button>
                                        </div>

                                        {/* 🔹 Full-width View Applications button */}
                                            <button
                                                className="view-applications-btn"
                                                onClick={() => navigate(`../opportunity/applications/${post.id}`)}
                                                disabled={isDeleted}
                                            >
                                                View Applications
                                            </button>


                                    </>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}






